import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider } from './Components/AuthContext';
import { createGlobalStyle } from 'styled-components';
import Navbar from './Components/Navbar';
import LoginRegistration from './Components/Login';
import HotelPage from './Pages/Hotel/HotelPage';
import DetailedPackageView from './Components/DetailedPackageView';
import About from './Components/About';
import Privacy from './Components/Privacy';
import Contact from './Components/Contact';
import ToS from './Components/ToS';
import MobileHome from './Components/MobileHome';
import Banner from './Components/Banner';
import Activities from './Components/Activities/Activities';
import Hotels from './Components/Hotels';
import Packages from './Components/Packages';
import PurchaseScreen from './Components/Purchase';
import Account from './Components/Account';
import PaymentDone from './Components/PaymentDone';
import './App.css';
import PersonalDetails from './Components/PersonalDetails';
import MobileHotels from './Components/MobileHotels';
import FeatureCarousel from './Components/FeatureCarousel';
import Loading from './loading';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
`;

const App = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchLocation, setSearchLocation] = useState('');
  const [selectedLocationType, setSelectedLocationType] = useState(null);
  const [showPackages, setShowPackages] = useState(isMobile);
  const [isLoading, setIsLoading] = useState(true);
  const [noHotelsMessage, setNoHotelsMessage] = useState('');

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      setShowPackages(newIsMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Simulate a 3-second loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (location, locationType) => {
    setSearchLocation(location);
    setSelectedLocationType(locationType);
    setNoHotelsMessage('');
  };

  const handleThemeChange = (theme) => {
    setSelectedLocationType(theme);
    setSearchLocation('');
    setNoHotelsMessage('');
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <GlobalStyle />
        <Router>
          <Navbar />
          <Routes>
            {/* Mobile-specific routes */}
            {isMobile ? (
              <>
                <Route path="/" element={<MobileHome />} />
                <Route path="/hotels" element={<MobileHotels />} />
              </>
            ) : (
              // Desktop route
              <Route path="/" element={
                <DesktopHome
                  showPackages={showPackages}
                  searchLocation={searchLocation}
                  selectedLocationType={selectedLocationType}
                  onLocationSelect={handleSearch}
                  onLocationTypeSelect={handleThemeChange}
                  noHotelsMessage={noHotelsMessage}
                />
              } />
            )}

            {/* Common routes for both mobile and desktop */}
            <Route path="/login" element={<LoginRegistration />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/purchase" element={<PurchaseScreen />} />
            <Route path="/account" element={<Account />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<ToS />} />
            <Route path="/hotel/:id" element={<HotelPage />} />
            <Route path="/packagedetails/:id" element={<DetailedPackageView />} />
            <Route path="/payment-done" element={<PaymentDone />} />
            <Route path="/details" element={<PersonalDetails />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

const DesktopHome = ({ 
  showPackages, 
  searchLocation, 
  selectedLocationType, 
  onLocationSelect, 
  onLocationTypeSelect,
  noHotelsMessage 
}) => (
  <>
    {showPackages ? (
      <Packages />
    ) : (
      <>
        <Banner 
          onLocationSelect={onLocationSelect} 
          onLocationTypeSelect={onLocationTypeSelect} 
        />
        <FeatureCarousel />
        <Activities selectedLocation={searchLocation} />
        <Hotels 
          searchLocation={searchLocation} 
          selectedLocationType={selectedLocationType} 
          noHotelsMessage={noHotelsMessage}
        />
        <Packages />
      </>
    )}
  </>
);

export default App;